/**
 * useTokenRefresh Hook
 *
 * Provides token refresh functionality:
 * - Proactive refresh before expiry (5 minutes before)
 * - Reactive refresh on TOKEN_EXPIRED errors
 * - Multi-tab synchronization via localStorage events
 */

import { useCallback, useEffect, useRef } from 'react'

const TOKEN_REFRESH_MARGIN_MS = 5 * 60 * 1000 // Refresh 5 minutes before expiry
const REFRESH_LOCK_KEY = 'token_refresh_lock'
const REFRESH_LOCK_TIMEOUT = 10000 // 10 seconds

export function useTokenRefresh() {
  const refreshingRef = useRef(false)
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  /**
   * Parse JWT token to get expiration time
   */
  const getTokenExpiry = useCallback((token: string): number | null => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      return payload.exp ? payload.exp * 1000 : null
    } catch {
      return null
    }
  }, [])

  /**
   * Acquire lock for refresh (prevent race conditions across tabs)
   */
  const acquireRefreshLock = useCallback((): boolean => {
    const lockValue = localStorage.getItem(REFRESH_LOCK_KEY)
    if (lockValue) {
      const lockTime = parseInt(lockValue, 10)
      if (Date.now() - lockTime < REFRESH_LOCK_TIMEOUT) {
        return false // Another tab is refreshing
      }
    }
    localStorage.setItem(REFRESH_LOCK_KEY, Date.now().toString())
    return true
  }, [])

  /**
   * Release refresh lock
   */
  const releaseRefreshLock = useCallback(() => {
    localStorage.removeItem(REFRESH_LOCK_KEY)
  }, [])

  /**
   * Refresh the current authentication token
   */
  const refreshToken = useCallback(async (): Promise<boolean> => {
    const token = localStorage.getItem('auth_token')
    if (!token) {
      console.warn('[useTokenRefresh] No token found to refresh')
      return false
    }

    // Prevent concurrent refreshes
    if (refreshingRef.current) {
      return false
    }

    // Acquire lock for multi-tab safety
    if (!acquireRefreshLock()) {
      return false
    }

    refreshingRef.current = true

    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

      const response = await fetch(`${API_URL}/api/auth/refresh-token`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        console.warn('[useTokenRefresh] Token refresh failed:', response.status, errorData)

        // If token is too old or invalid, need full re-login
        if (errorData.code === 'TOKEN_TOO_OLD' || errorData.code === 'USER_NOT_FOUND') {
          console.warn('[useTokenRefresh] Token cannot be refreshed, logout required')
          return false
        }

        return false
      }

      const { token: newToken, user } = await response.json()

      if (!newToken) {
        console.warn('[useTokenRefresh] No token received in response')
        return false
      }

      // Update localStorage with new token
      localStorage.setItem('auth_token', newToken)
      if (user) {
        localStorage.setItem('user_data', JSON.stringify(user))
      }

      // Dispatch storage event for other tabs
      window.dispatchEvent(new StorageEvent('storage', {
        key: 'auth_token',
        newValue: newToken
      }))

      return true
    } catch (err) {
      console.warn('[useTokenRefresh] Token refresh error:', err)
      return false
    } finally {
      refreshingRef.current = false
      releaseRefreshLock()
    }
  }, [acquireRefreshLock, releaseRefreshLock])

  /**
   * Schedule proactive token refresh
   */
  const scheduleRefresh = useCallback(() => {
    const token = localStorage.getItem('auth_token')
    if (!token) return

    const expiry = getTokenExpiry(token)
    if (!expiry) return

    const timeUntilExpiry = expiry - Date.now()
    const refreshTime = timeUntilExpiry - TOKEN_REFRESH_MARGIN_MS

    if (refreshTime <= 0) {
      // Token already expired or about to expire, refresh now
      refreshToken()
      return
    }

    // Clear existing timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current)
    }

    refreshTimerRef.current = setTimeout(() => {
      refreshToken().then(success => {
        if (success) {
          scheduleRefresh() // Schedule next refresh
        }
      })
    }, refreshTime)
  }, [getTokenExpiry, refreshToken])

  /**
   * Check if token needs refresh (for manual checks)
   */
  const needsRefresh = useCallback((): boolean => {
    const token = localStorage.getItem('auth_token')
    if (!token) return false

    const expiry = getTokenExpiry(token)
    if (!expiry) return false

    return (expiry - Date.now()) < TOKEN_REFRESH_MARGIN_MS
  }, [getTokenExpiry])

  // Schedule refresh on mount and listen for storage changes
  useEffect(() => {
    scheduleRefresh()

    // Listen for storage events from other tabs
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'auth_token' && e.newValue) {
        scheduleRefresh()
      }
    }

    window.addEventListener('storage', handleStorageChange)

    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current)
      }
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [scheduleRefresh])

  return { refreshToken, scheduleRefresh, needsRefresh }
}
